import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./client";
import { useAuthStore } from "@/store/authStore";

export interface MeClient {
  id: string;
  name: string;
  slug: string;
  timezone: string;
  default_currency: string;
}

/**
 * Branding block — resolved by the backend via customer > client > platform
 * fallback. BrandingProvider consumes these values and sets CSS vars /
 * document.title accordingly.
 */
export interface MeBranding {
  primary_color: string;
  secondary_color: string;
  app_name: string;
  email_sender_name: string;
  logo_s3_key: string | null;
  favicon_s3_key: string | null;
  email_header_logo_s3_key: string | null;
  // P4 B28 — three logo variants picked per (collapsed × theme) by the
  // Sidebar consumer (ui.md §7.3).  Missing-variant fallback climbs:
  // mini-dark → mini-light → full-dark → full-light.
  mini_logo_s3_key: string | null;
  logo_dark_s3_key: string | null;
  mini_logo_dark_s3_key: string | null;
}

export type PrimaryPersona = "SuperAdmin" | "ClientAdmin" | "OM" | "SiteOwner";

export interface MeResponse {
  id: string;
  email: string;
  full_name: string;
  cognito_group: "SuperAdmin" | "ClientAdmin" | "User";
  /**
   * Server-derived persona — SuperAdmin / ClientAdmin straight from
   * cognito_group; `User` split into `OM` vs `SiteOwner` by policy-feature
   * signature. Used by DashboardRouter (ui.md §9.4) and Sidebar filtering.
   */
  primary_persona: PrimaryPersona;
  status: string;
  locale: string;
  email_verified: boolean;
  first_login_at: string | null;
  last_login_at: string | null;
  default_timezone: string | null;
  phone: string | null;
  avatar_s3_key: string | null;
  /**
   * Short-lived (5-min) presigned GET URL for the caller's avatar. Render
   * via `<img src={me.avatar_url}>`. Regenerated on every `/me` response;
   * staleTime on the query (5 min) aligns with the presign TTL so the
   * URL stays fresh. null when the user hasn't uploaded an avatar.
   */
  avatar_url: string | null;
  client: MeClient;
  branding: MeBranding;
  /**
   * MV2-186: server-resolved union of policy features. SuperAdmin gets
   * the full backend FEATURES set (including SuperAdmin-only); other
   * roles get the union of attached active policies' features minus
   * SuperAdmin-only entries.
   *
   * MV2-243: this is now the **global union** across home attachments
   * and every cross-client grant — use it for nav / Overview / Map /
   * any surface with no client context. For widgets gated on a site
   * page, prefer `useHasFeature(f, { clientId: site.client_id })`
   * which resolves through `attachments` / `cross_client_grants` and
   * gives the right answer when the user has different policies on
   * home vs cross-client. Sorted server-side for deterministic cache
   * key behavior across refetches.
   *
   * Optional in the type so legacy clients reading a stale schema
   * don't crash; new code should treat undefined as "no features"
   * (most restrictive). The backend always populates it.
   */
  features?: string[];
  /**
   * MV2-243: home-client policy attachments — one entry per attached
   * policy with its feature set. Effective features on the home
   * client = union of every attachment's `features`. Used by
   * `useFeaturesForClient(home_client_id)` / `useHasFeature(f,
   * { clientId })` for per-client gating.
   */
  attachments?: AttachmentInMe[];
  /**
   * MV2-243: active cross-client grants. Each grant carries the
   * foreign client_id, the narrowed site_ids (empty = wildcard within
   * that client), the union of features from policies attached to the
   * grant, and the inviter-set "access valid until" date (null =
   * open-ended). The UI picks the right grant by matching a site's
   * `client_id`.
   */
  cross_client_grants?: CrossClientGrantInMe[];
}

/**
 * MV2-243: shape of one ``attachments[]`` entry on ``/me``.
 *
 * Pure read-model. The UI only needs the policy_id (to render the
 * "attached policies" chips) and the features (for per-client gating
 * via `useHasFeature(f, { clientId: home_client_id })`).
 */
export interface AttachmentInMe {
  policy_id: string;
  features: string[];
  report_types: string[];
}

/**
 * MV2-243: shape of one ``cross_client_grants[]`` entry on ``/me``.
 *
 * `access_end_date` is the inviter-set "access valid until" date that
 * was promoted from `cross_client_invitations.access_end_date` on
 * accept. Renders the "valid until" badge on the user-detail
 * cross-client section. Empty `site_ids` means the grant covers every
 * site under `client_id`.
 */
export interface CrossClientGrantInMe {
  grant_id: string;
  client_id: string;
  site_ids: string[];
  features: string[];
  access_end_date: string | null;
  /** MV2-869 — "through ACEL Energy", not a UUID. */
  client_name?: string | null;
  site_names?: string[];
}

/**
 * GET /me — current user profile. The backend auto-provisions the users row
 * on first call (see monitoring-backend app/dependencies.py::_auto_provision),
 * so a fresh Cognito account lands here and gets a DB row + home client.
 *
 * The queryKey includes `actingAs.clientId` so that entering or exiting
 * impersonation (SuperAdmin only) triggers a refetch — without it, the
 * cached `/me` (with its embedded branding + client blocks) stays pinned
 * to the pre-impersonation values, so the Sidebar logo + document title +
 * CSS branding vars never update. See Slice 5 bundled fixes.
 */
// DEV-ONLY fixture — used when __dev_bypass=1 is set in localStorage.
const DEV_ME_FIXTURE: MeResponse | null =
  import.meta.env.DEV &&
  typeof localStorage !== "undefined" &&
  localStorage.getItem("__dev_bypass") === "1"
    ? {
        id: "dev-user-1",
        email: "dev@rebot.ie",
        full_name: "Dev User",
        cognito_group: "SuperAdmin",
        primary_persona: "SuperAdmin",
        status: "active",
        locale: "en",
        email_verified: true,
        first_login_at: null,
        last_login_at: null,
        default_timezone: "Europe/Dublin",
        phone: null,
        avatar_s3_key: null,
        avatar_url: null,
        client: {
          id: "client-1",
          name: "re:bot",
          slug: "rebot",
          timezone: "Europe/Dublin",
          default_currency: "EUR",
        },
        branding: {
          primary_color: "#16a34a",
          secondary_color: "#0c2d49",
          app_name: "re:bot Monitoring",
          email_sender_name: "re:bot",
          logo_s3_key: null,
          favicon_s3_key: null,
          email_header_logo_s3_key: null,
          mini_logo_s3_key: null,
          logo_dark_s3_key: null,
          mini_logo_dark_s3_key: null,
        },
      }
    : null;

export function useMe(options: { enabled?: boolean } = {}) {
  const actingAsClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  return useQuery<MeResponse>({
    queryKey: ["me", actingAsClientId],
    queryFn: async () => {
      if (DEV_ME_FIXTURE) return DEV_ME_FIXTURE;
      return (await apiClient.get<MeResponse>("/me")).data;
    },
    enabled: options.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/**
 * MV2-186 + MV2-243: does the current user have a given policy feature?
 *
 * Two modes:
 *
 *   * **Global mode** (no options) — checks the global-union
 *     ``me.features``. Use for nav / Overview / Map / any surface
 *     that has no specific client context.
 *
 *   * **Per-client mode** (`{ clientId }`) — resolves the user's
 *     effective features for that client (home attachments if
 *     `clientId === me.client.id`; matching cross-client grant
 *     otherwise) and checks against that set. Use for widgets gated
 *     on a specific site page — pass `site.client_id`.
 *
 * Returns `false` while `/me` is loading so callers fail-closed (UI
 * hides the gated surface until we know).
 */
export function useHasFeature(
  feature: string,
  options?: { clientId?: string | null },
): boolean {
  const { data } = useMe();
  if (!data) return false;
  const set = options?.clientId
    ? featuresForClient(data, options.clientId)
    : new Set(data.features ?? []);
  return set.has(feature);
}

/**
 * Same as ``useHasFeature`` but checks any-of — useful for surfaces
 * that two adjacent features both unlock (e.g. Reports tab is fine
 * with either ``generate_report`` or ``subscribe_report``).
 *
 * MV2-243: also accepts an optional `{ clientId }` for per-client
 * resolution. Either-arity overloads supported — pass features as
 * varargs, or pass `(features[], { clientId })`.
 */
export function useHasAnyFeature(
  features: readonly string[],
  options?: { clientId?: string | null },
): boolean;
export function useHasAnyFeature(...features: string[]): boolean;
export function useHasAnyFeature(
  first: readonly string[] | string,
  ...rest: readonly (string | { clientId?: string | null } | undefined)[]
): boolean {
  const { data } = useMe();
  if (!data) return false;
  let features: readonly string[];
  let clientId: string | null | undefined;
  if (Array.isArray(first)) {
    features = first as readonly string[];
    const opts = rest[0];
    if (opts && typeof opts === "object") clientId = opts.clientId;
  } else {
    features = [first as string, ...(rest as string[])];
  }
  const set = clientId ? featuresForClient(data, clientId) : new Set(data.features ?? []);
  return features.some((f) => set.has(f));
}

/**
 * MV2-243: convenience hook — return the effective feature set for
 * a given client. Resolves home attachments when ``clientId``
 * matches the user's home client; otherwise scans
 * ``cross_client_grants`` for a matching grant. Empty Set when the
 * user has no access to ``clientId`` (e.g. wrong client_id passed by
 * a router that hasn't loaded the site yet).
 */
export function useFeaturesForClient(clientId: string | null | undefined): Set<string> {
  const { data } = useMe();
  if (!data || !clientId) return new Set();
  return featuresForClient(data, clientId);
}

/**
 * Pure helper — given a /me payload + a client_id, return the
 * effective feature set for that client. Mirrors
 * ``PermissionScope.features_for_client(...)`` on the backend.
 */
function featuresForClient(me: MeResponse, clientId: string): Set<string> {
  if (me.cognito_group === "SuperAdmin") {
    return new Set(me.features ?? []);
  }
  if (clientId === me.client.id) {
    const out = new Set<string>();
    for (const a of me.attachments ?? []) {
      for (const f of a.features) out.add(f);
    }
    return out;
  }
  for (const g of me.cross_client_grants ?? []) {
    if (g.client_id === clientId) return new Set(g.features);
  }
  return new Set();
}

// -------------------------------------------------------------------------- //
// Profile + notifications self-service — Slice 2b
// -------------------------------------------------------------------------- //

export interface MyProfileUpdate {
  full_name?: string | null;
  default_timezone?: string | null;
  locale?: string | null;
  avatar_s3_key?: string | null;
  phone?: string | null;
}

export function useUpdateMyProfile() {
  const qc = useQueryClient();
  const actingAsClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  return useMutation<MeResponse, Error, MyProfileUpdate>({
    mutationFn: async (body) =>
      (await apiClient.patch<MeResponse>("/me/profile", body)).data,
    onSuccess: (data) => {
      // Keep the cached `/me` in sync with the updated row. Key must
      // include the current acting-as client so we hit the active entry.
      qc.setQueryData(["me", actingAsClientId], data);
    },
  });
}

export interface NotificationPreferences {
  user_id: string;
  email_enabled: boolean;
  // MV2-216: SMS dropped platform-wide (Alembic 0056). Channels are
  // in_app + email + webhook only.
  in_app_enabled: boolean;
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
  quiet_hours_timezone: string | null;
  suppress_non_critical_during_quiet: boolean;
  trigger_preferences: Record<string, unknown>;
  // MV2-223: opt-out for the MV2-214 auto-watch-on-comment behaviour.
  // Default true preserves the current contract — every existing user
  // keeps getting auto-subscribed when they comment on a ticket.
  auto_watch_on_comment: boolean;
  updated_at: string;
}

export interface NotificationPreferencesUpdate {
  email_enabled?: boolean;
  in_app_enabled?: boolean;
  quiet_hours_start?: string | null;
  quiet_hours_end?: string | null;
  quiet_hours_timezone?: string | null;
  suppress_non_critical_during_quiet?: boolean;
  trigger_preferences?: Record<string, unknown>;
  auto_watch_on_comment?: boolean;
}

export function useMyNotifications() {
  return useQuery<NotificationPreferences>({
    queryKey: ["me", "notifications"],
    queryFn: async () =>
      (await apiClient.get<NotificationPreferences>("/me/notifications")).data,
    staleTime: 60_000,
  });
}

export function useUpdateMyNotifications() {
  const qc = useQueryClient();
  return useMutation<NotificationPreferences, Error, NotificationPreferencesUpdate>({
    mutationFn: async (body) =>
      (await apiClient.patch<NotificationPreferences>("/me/notifications", body)).data,
    onSuccess: (data) => {
      qc.setQueryData(["me", "notifications"], data);
    },
  });
}

// --------------------------------------------------------------------------- //
// MV2-224 — preview / reset / send-test
// --------------------------------------------------------------------------- //

/**
 * Wipe per-event matrix preferences back to platform defaults.
 * Channel master toggles + auto_watch_on_comment are preserved — only
 * trigger_preferences is reset.
 */
export function useResetMyNotifications() {
  const qc = useQueryClient();
  return useMutation<NotificationPreferences, Error, void>({
    mutationFn: async () =>
      (await apiClient.post<NotificationPreferences>("/me/notifications/reset")).data,
    onSuccess: (data) => {
      qc.setQueryData(["me", "notifications"], data);
    },
  });
}

export interface NotificationEmailPreview {
  subject: string;
  html_body: string;
  text_body: string;
}

export interface NotificationPreviewResponse {
  event_id: string;
  category: string;
  title: string;
  snippet: string;
  related_type: string | null;
  has_email_template: boolean;
  email: NotificationEmailPreview | null;
  email_render_error: string | null;
}

/**
 * Live preview of a sample notification for a matrix event. Email body
 * is SES-rendered against the caller's branding so it matches what a
 * real send would produce. Lazy — only fired when a preview modal opens.
 */
export function useNotificationPreview(eventId: string | null) {
  return useQuery<NotificationPreviewResponse>({
    queryKey: ["me", "notifications", "preview", eventId],
    enabled: !!eventId,
    queryFn: async () =>
      (
        await apiClient.get<NotificationPreviewResponse>(
          `/me/notifications/preview/${eventId}`,
        )
      ).data,
    // Previews are deterministic per (user, event_id) tuple — cache
    // for the session so reopening the modal doesn't refetch.
    staleTime: 5 * 60_000,
  });
}

export interface SendTestRequest {
  event_id: string;
  channels: ("in_app" | "email")[];
}

export interface SendTestResponse {
  event_id: string;
  sent_in_app: boolean;
  sent_email: boolean;
  notification_log_id: string | null;
}

/**
 * Fire a real test notification through the dispatcher with a [TEST]
 * subject prefix. Rate-limited 5/hour at the backend; UI shows a toast
 * on the 403.
 */
export function useSendTestNotification() {
  const qc = useQueryClient();
  return useMutation<SendTestResponse, Error, SendTestRequest>({
    mutationFn: async (body) =>
      (await apiClient.post<SendTestResponse>("/me/notifications/test", body)).data,
    onSuccess: () => {
      // Bell badge + feed should reflect the test row immediately.
      void qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}
